import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  getAsksService,
  getRatingsService,
  patchCloseAskService,
  patchRatingPublishService,
  postAskReplyService,
  postDealUpdateService,
  postAskService,
  postRatingService,
} from "@/services/ratingsAsks.service";
import { IAPIResponse } from "@/services/api.types";
import {
  Ask,
  AskFormData,
  AskReply,
  AskReplyFormData,
  DealUpdateFormData,
  Rating,
  RatingFormData,
  RatingPublishPayload,
} from "@/types/ratingsAsks.types";

export const ratingsAsksQueryKeys = {
  all: () => ["ratingsAsks"] as const,
  ratings: () => [...ratingsAsksQueryKeys.all(), "ratings"] as const,
  ratingsByDeal: (dealId: string) => [...ratingsAsksQueryKeys.ratings(), dealId] as const,
  asks: () => [...ratingsAsksQueryKeys.all(), "asks"] as const,
  asksByDeal: (dealId: string) => [...ratingsAsksQueryKeys.asks(), dealId] as const,
};

// ============ Ratings ============
export const useGetRatings = (dealId: string) => {
  return useQuery<Rating[], Error>({
    queryKey: ratingsAsksQueryKeys.ratingsByDeal(dealId),
    queryFn: () => getRatingsService(dealId),
    enabled: !!dealId,
  });
};

export const usePostRating = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Rating>, Error, RatingFormData>({
    mutationFn: payload => postRatingService(dealId, payload),
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ratingsAsksQueryKeys.ratingsByDeal(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const usePatchRatingPublish = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, Error, { ratingId: string; payload: RatingPublishPayload }>({
    mutationFn: ({ ratingId, payload }) => patchRatingPublishService(ratingId, payload),
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ratingsAsksQueryKeys.ratingsByDeal(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// ============ Asks ============
export const useGetAsks = (dealId: string) => {
  return useQuery<Ask[], Error>({
    queryKey: ratingsAsksQueryKeys.asksByDeal(dealId),
    queryFn: () => getAsksService(dealId),
    enabled: !!dealId,
  });
};

export const usePostAsk = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Ask>, Error, AskFormData>({
    mutationFn: payload => postAskService(dealId, payload),
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ratingsAsksQueryKeys.asksByDeal(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

// An agent opening a thread with their client. The server writes it into the
// same collection as a client's ask, so this invalidates the same query key and
// the new thread appears in the list alongside everything else.
export const usePostDealUpdate = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Ask>, Error, DealUpdateFormData>({
    mutationFn: payload => postDealUpdateService(dealId, payload),
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ratingsAsksQueryKeys.asksByDeal(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const usePostAskReply = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<AskReply>, Error, { askId: string; payload: AskReplyFormData }>({
    mutationFn: ({ askId, payload }) => postAskReplyService(askId, payload),
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ratingsAsksQueryKeys.asksByDeal(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const usePatchCloseAsk = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ ok: boolean }>, Error, string>({
    mutationFn: (askId: string) => patchCloseAskService(askId),
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: ratingsAsksQueryKeys.asksByDeal(dealId) });
    },
    onError: err => toast.error(err.message),
  });
};
